/*
  File: AgentTree.tsx
  Author: Binish Mookken
  Date: April-June 2024

  Description:
  This file contains the AgentTree, which renders a tree structure representing the agent network.

  Components:
  - AgentTree: Main component for rendering the agent network tree.

  Props:
  - agentInstance: Data representing the agent instance.

  Functions:
  - renderTreeNodes: Recursively renders tree nodes.
  - buildTree: Builds the tree structure from the agent data.

  Dependencies:
  - antd: Provides the Tree component for rendering the tree structure.
*/

import React from "react";
import { Avatar, Tree } from "antd";
import { AgentInstance } from "./types";
import "./AgentTree.css";
import { getNameAbbrevation } from "./service/service";

interface TreeComponentProps {
  agentInstance: AgentInstance | null;
}

const { TreeNode } = Tree;

const AgentTree: React.FC<TreeComponentProps> = ({
  agentInstance: agentInstance,
}) => {
  const tree = agentInstance?.tree;

  // Function to get color based on depth level
  const getColorByDepth = (depth: number) => {
    switch (depth) {
      case 0:
        return "#15f62b"; // Level 0
      case 1:
        return "#cf88f5"; // Level 1
      case 2:
        return "#4fa8ff"; // Level 2
      case 3:
        return "#d89614"; // Level 3
      default:
        return "#ffffff"; // Default color for other levels
    }
  };

  const renderTreeNodes = (nodes: any[], depth: number = 0) =>
    nodes.map((node) => (
      <TreeNode
        key={node.id}
        title={
          <div style={{ display: "flex", alignItems: "center" }}>
            <Avatar
              style={{ height: "25px", width: "25px", marginRight: "4px" }}
            >
              {getNameAbbrevation(node.first_name, node.last_name)}
            </Avatar>
            <span
              style={{
                color: getColorByDepth(depth),
                fontSize: "16px",
                fontFamily: "Ubuntu",
              }}
            >
              {`${node.first_name} ${node.last_name}`}
            </span>
          </div>
        }
      >
        {node.children && renderTreeNodes(node.children, depth + 1)}
      </TreeNode>
    ));

  const buildTree = (data: any) => {
    const dataArray = Object.values(data) as any[][];
    const treeData: any[] = [];
    const map: any = {};

    // Iterate over each array and create a map for efficient lookup
    dataArray.forEach((arr: any[]) => {
      arr.forEach((node) => {
        map[node.id] = node;
        map[node.id].children = [];
      });
    });

    // Iterate over each array again and build the tree structure
    dataArray.forEach((arr: any[]) => {
      arr.forEach((node) => {
        if (node.referred_by_id && map[node.referred_by_id]) {
          // Check if parent node exists
          map[node.referred_by_id].children.push(node);
        } else {
          treeData.push(node);
        }
      });
    });

    return treeData;
  };

  return (
    <div>
      <div
        style={{
          backgroundColor: "rgb(51, 51, 51)", // Dark background color
          boxShadow: "rgba(0, 0, 0, 0.3) 0px 2px 4px", // Shadow effect
          padding: "2px 10px", // Padding
          color: "rgb(255, 137, 48)", // Text color
          marginTop: "20px", // Margin from the top
        }}
      >
        <span
          style={{
            fontSize: "15px",
            fontWeight: "700",
            display: "block",
            fontFamily: "ubuntu",
            padding: "4px 5px",
          }}
        >
          My Passive Income Network
        </span>
      </div>
      <br />
      {tree && (
        <Tree showLine defaultExpandAll>
          {renderTreeNodes(buildTree(tree))}
        </Tree>
      )}
    </div>
  );
};

export default AgentTree;
